import React from "react";
import { Modal, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { PlusCircle, X } from "lucide-react";

import HomeSimulate from "../simulate/HomeSimulate";
import { RainbowBorder } from "../../../common/native";
import { useOnboardingTips } from "../../../../contexts/OnboardingTipsContext";
import { TOOLTIP_STEPS } from "../../../../utils/constant";

type TPatientRef = {
  clinicOSUUID?: string;
  visitType?: string;
};

type SimulateConsultsProps = {
  patient: TPatientRef | null;
  suggestedImageUrls?: string[];
  tipsEnabled?: boolean;
};

const SimulateConsults: React.FC<SimulateConsultsProps> = ({
  patient,
  suggestedImageUrls = [],
  tipsEnabled = false,
}) => {
  const [visible, setVisible] = React.useState(false);
  const { tipsState, markStepComplete } = useOnboardingTips();

  const isFollowUp =
    Boolean(patient?.visitType) &&
    (patient?.visitType?.includes("F/U") ||
      patient?.visitType?.toLowerCase().includes("follow up"));

  if (isFollowUp) return null;

  const canOpenSimulation = Boolean(patient?.clinicOSUUID);

  const handleOpen = () => {
    if (tipsEnabled && tipsState.simulateInConsult) {
      markStepComplete(TOOLTIP_STEPS.SIMULATE_IN_CONSULT);
    }
    setVisible(true);
  };

  return (
    <View>
      <Text style={styles.subtitle}>
        Use the full Simulate UI for this patient.
      </Text>

      <RainbowBorder
        isActive={tipsEnabled && tipsState.simulateInConsult}
        borderRadius={12}
      >
        <TouchableOpacity
          style={[
            styles.generateButton,
            !canOpenSimulation && styles.generateButtonDisabled,
          ]}
          onPress={handleOpen}
          disabled={!canOpenSimulation}
        >
          <PlusCircle size={20} color="#FFFFFF" />
          <Text
            style={[
              styles.generateButtonText,
              !canOpenSimulation && styles.generateButtonTextDisabled,
            ]}
          >
            Generate Simulation
          </Text>
        </TouchableOpacity>
      </RainbowBorder>

      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={() => setVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Generate Simulation</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setVisible(false)}
              >
                <X size={20} color="#1A1A1A" />
              </TouchableOpacity>
            </View>

            <HomeSimulate
              patientId={patient?.clinicOSUUID}
              suggestedImageUrls={suggestedImageUrls}
              showTitle={false}
              showTipsToggle={false}
              enableTips={false}
            />
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  subtitle: {
    fontSize: 13,
    color: "#6B7280",
    marginBottom: 12,
  },
  generateButton: {
    minHeight: 46,
    borderRadius: 10,
    backgroundColor: "#00B7F4",
    paddingVertical: 12,
    paddingHorizontal: 24,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    maxWidth: 300,
  },
  generateButtonDisabled: {
    backgroundColor: "#C4C6CD",
  },
  generateButtonText: {
    color: "#FFFFFF",
    fontFamily: "Urbanist500",
    textAlign: "center",
    fontSize: 16,
    lineHeight: 22.4,
    letterSpacing: 0.032,
  },
  generateButtonTextDisabled: {
    color: "#FFFFFF",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.35)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalContent: {
    width: "100%",
    maxWidth: 1180,
    maxHeight: "92%",
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#E6E6E6",
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#ECECEC",
    backgroundColor: "#FAFAFA",
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#1F2937",
  },
  modalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F2F4F7",
  },
});

export default SimulateConsults;
